// apps/vendor/src/features/products/variants/types.ts
//
// Shared variant-grid types used by VariantMatrix + ColorImageGroup +
// MatrixCellEditor + Add Product wizard.

export interface VariantDimensionsDraft {
  length: number;
  width: number;
  height: number;
}

export interface VariantDraft {
  size: string;
  color: string;
  enabled: boolean;
  cost_markup_cents: number;
  /**
   * 2026-10-14 (D13/D17) — the SKU's own cost (file import). Set on a product
   * priced per SKU, where it replaces the markup; absent/null otherwise.
   */
  cost_cents?: number | null;
  image_urls: string[];
  weight_grams_override?: number;
  /** Bug 7 — per-variant packaging dimensions (cm). Required on submit. */
  dimensions: VariantDimensionsDraft;
  /** Amendment #4 — per-SKU operational fields (required on submit). */
  processing_time_days: number;
  capacity_per_day: number;
  weight_grams: number;
  /** Amendment #4 — external-system SKU mapping (optional). */
  external_sku?: string;
  /** Amendment #4 — chosen blueprint option-value refs (catalog path). */
  option_value_ids?: string[];
  /** Amendment #4 — blueprint-derived SKU (read-only, catalog path). */
  sku?: string;
  /**
   * 2026-10-13 — the Printway variant this vendor SKU fulfils (file import).
   * Fixed once set: relabelling keeps it, the server keys mapped rows on it.
   */
  printway_variant_id?: string;
}

export interface MatrixModel {
  sizes: string[];
  colors: string[];
  /** Map keyed by `${size}__${color}` for O(1) lookup. */
  cells: Record<string, VariantDraft>;
}

export function cellKey(size: string, color: string): string {
  return `${size}__${color}`;
}

export function emptyMatrix(): MatrixModel {
  return { sizes: [], colors: [], cells: {} };
}

export function ensureCells(matrix: MatrixModel): MatrixModel {
  const next = { ...matrix.cells };
  for (const size of matrix.sizes) {
    for (const color of matrix.colors) {
      const key = cellKey(size, color);
      if (!next[key]) {
        next[key] = {
          size,
          color,
          enabled: true,
          cost_markup_cents: 0,
          image_urls: [],
          dimensions: { length: 0, width: 0, height: 0 },
          // Amendment #4 (165) — per-SKU ops fields default 0;
          // external_sku empty so a fresh free-type cell is submit-valid.
          processing_time_days: 0,
          capacity_per_day: 0,
          weight_grams: 0,
          external_sku: '',
        };
      }
    }
  }
  return { ...matrix, cells: next };
}

export function variantsFromMatrix(matrix: MatrixModel): VariantDraft[] {
  const out: VariantDraft[] = [];
  for (const size of matrix.sizes) {
    for (const color of matrix.colors) {
      const c = matrix.cells[cellKey(size, color)];
      if (c) out.push(c);
    }
  }
  return out;
}
